import React from 'react';
import PropTypes from 'prop-types';
import _omit from 'lodash/omit';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { components as clientComponents } from 'mage-client';

import { analytics } from '../utils/analytics';
import FlowContext from './context';
import {
  getCurrentStep,
  getParamsFromPath,
  getPath,
  getStepNumberFromFlowSteps,
  resolveDirectionFromPaths,
  resolveThemeFromPaths,
} from './utils';
import { initFlow } from './initFlow';

export default function withConfiguredFlow(FLOW_THEMES) {
  return function withConfiguredFlowInner(WrappedComponent) {
    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    const wrappedDisplayName = `ConfiguredFlow(${displayName})`;

    class ConfiguredFlow extends React.Component {
      constructor(props) {
        super(props);
        this.state = {
          step: {
            isDisabled: false,
          },
          direction: 'next',
          prevPath: '',
        };

        this.handleNext = this.handleNext.bind(this);
        this.handleBack = this.handleBack.bind(this);
        this.initFlow = this.initFlow.bind(this);

        this.initFlow(props);
      }

      static getDerivedStateFromProps(props, state) {
        if (!state.prevPath) {
          const newPath = props.location.pathname;

          return {
            ...state,
            prevPath: newPath,
            theme: resolveThemeFromPaths('', newPath, props, FLOW_THEMES),
          };
        } else if (state.prevPath !== props.location.pathname) {
          const newPath = props.location.pathname;
          const oldPath = state.prevPath;
          const direction = resolveDirectionFromPaths(oldPath, newPath, props);

          const newState = {
            ...state,
            direction,
            prevPath: newPath,
            theme: resolveThemeFromPaths(oldPath, newPath, props, FLOW_THEMES),
          };

          return newState;
        }
        return null;
      }

      go(path) {
        if (!path) {
          return false;
        }
        return this.props.history.push(path + this.props.history.location.search);
      }

      handleNext() {
        const props = _omit(this.props, ['flowConfig']);
        const nextPath = getPath(this.flow, props, 'next');
        this.go(nextPath);
      }

      handleBack() {
        const props = _omit(this.props, ['flowConfig']);
        const backPath = getPath(this.flow, props, 'back');
        analytics.logEvent(analytics.ANALYTICS_EVENTS.backButton, {
          currentPage: window.location.pathname.toString(),
          destinationPage: backPath,
        });
        this.go(backPath);
      }

      initFlow() {
        this.flow = initFlow(this.props);
      }

      render() {
        const { extraContextProps, ...props } = _omit(this.props, ['flowConfig']);
        const currentStep = getCurrentStep(this.flow, props);

        if (!currentStep) {
          // If there is no current step, we try to init the flow and get a proper step,
          // there should always be a step or a fallback path
          this.initFlow(this.props);
          return null;
        }

        const { path, Container, extraProps = {}, back = false } = currentStep;

        const paramsFromPath = getParamsFromPath(this.flow, props);

        if (!Container) {
          return null;
        }

        const hasBack = !!getPath(this.flow, props, 'back');

        const currentStepNumber = getStepNumberFromFlowSteps(this.flow.steps, { path });
        const maxStep = getStepNumberFromFlowSteps(this.flow.steps);

        const navigation = {
          direction: this.state.direction,
          key: props.location.pathname,
          theme: this.state.theme,
        };

        return (
          <FlowContext.Provider
            value={{
              ...extraContextProps,
              back,
              basePath: props.basePath,
              currentStep: currentStepNumber,
              maxStep,
              navigation,
            }}
          >
            <WrappedComponent
              Container={Container}
              paramsFromPath={paramsFromPath}
              onNext={this.handleNext}
              hasBack={hasBack}
              onBack={this.handleBack}
              isDisabled={this.state.step.isDisabled}
              animation={navigation}
              initFlow={this.initFlow}
              {...props}
              {...extraProps}
            />
          </FlowContext.Provider>
        );
      }
    }

    ConfiguredFlow.displayName = wrappedDisplayName;
    ConfiguredFlow.WrappedComponent = WrappedComponent;
    ConfiguredFlow.propTypes = {
      appPath: PropTypes.string.isRequired,
      basePath: PropTypes.string.isRequired,
      flowConfig: PropTypes.object.isRequired,
      history: PropTypes.object.isRequired,
      extraContextProps: PropTypes.object,
      isInitialized: PropTypes.bool,
      location: PropTypes.object,
    };

    return withRouter(
      compose(
        clientComponents.withPromiseBasedCapabilities(
          ({ flowConfig = {} }) => flowConfig.neededCaps || {},
        ),
        connect((state, { flowConfig = {} }) => {
          const mappedState = {};
          Object.keys(flowConfig.selectors || {}).map(key => {
            mappedState[key] = flowConfig.selectors[key](state);
          });
          return mappedState;
        }),
      )(ConfiguredFlow),
    );
  };
}
